<template>
  <div>
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script setup>
import {
  ApolloClient,
  ApolloLink,
  concat,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client/core";
import { createApp, provide, h, computed } from "vue";
import {
  DefaultApolloClient,
  provideApolloClient,
} from "@vue/apollo-composable";
import { useLassoCRM } from "~/composables/useLassoCRM";
import { GET_META } from "~/graphql/GetMeta.js";

const config = useRuntimeConfig();
const graphqlUrl = config.public.GRAPHQL_URL;

// HTTP connection to the API
const httpLink = createHttpLink({
  uri: graphqlUrl,
});

// Cache implementation
const cache = new InMemoryCache({
  typePolicies: {
    Page_Hlorganisms_hlOrganism: {
      // ToDo: Implement automatic unique ID ACF field for organisms
      // Since there is no obvious unique id field, and the id field
      // returns null, the Apollo cache will repeat the first organism
      // over and over because it can't tell the difference.
      // Here we define keyFields for the cache, hoping that every
      // organism has a unique combination of the following.
      // This should work for now, but maybe either revisit this
      // policy, or define a truly unique id field to use instead.
      //
      // Somebody made a plugin for this, but it hasn't been updated in
      // 8 years. One possible approach though:
      // https://github.com/KLicheR/wp-acf-unique_id
      keyFields: [
        "type",
        "title1",
        "title2",
        "title3",
        "text1",
        "id",
        "cssClasses",
        "description",
        "item",
      ],
    },
    Page_Hlorganisms_hlOrganism_item: {
      keyFields: [
        "type",
        "title1",
        "title2",
        "title3",
        "text1",
        "id",
        "cssClasses",
        "description",
        "image",
        "imageLink",
      ],
    },
    Campaign_Hlorganisms_hlOrganism_item: {
      keyFields: [
        "type",
        "title1",
        "title2",
        "title3",
        "text1",
        "id",
        "cssClasses",
        "description",
        "image",
        "imageLink",
      ],
    },
    User: {
      keyFields: ["id", "databaseId", "favorites"],
    },
    Neighborhood_Neighborhood: {
      merge: true,
    },
    Model_Modeldetails: {
      merge: true,
    },
    Plan_Floorplandetails: {
      merge: true,
    },
    Event: {
      keyFields: ["id", "databaseId", "uri"],
    },
  },
});

// Middleware for adding the Authorization token to headers
const authMiddleware = new ApolloLink((operation, forward) => {
  const cookie = useCookie("apollo:favorites.token");
  const token = cookie.value;

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  }));

  return forward(operation);
});

const nuxtApp = useNuxtApp();
const { $apolloErrorLink } = nuxtApp;

const apolloClient = new ApolloClient({
  link: ApolloLink.from([$apolloErrorLink, concat(authMiddleware, httpLink)]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-first",
    },
    query: {
      fetchPolicy: "cache-first",
    },
  },
});

const app = createApp({
  setup() {
    provide(DefaultApolloClient, apolloClient);
  },
  render: () => h(App),
});

provideApolloClient(apolloClient);

const route = useRoute();

const siteUrl = "https://consort-homes.com";
const ogImageUrl = `${siteUrl}/img/logo-meta.png`;

// Client-side only meta tags (won't be seen by crawlers, but set for consistency)
// The server middleware will inject proper meta tags for bots
onMounted(() => {
  const { result, loading } = useQuery(GET_META, { uri: route.path });

  watch([result, loading], () => {
    if (!loading.value && result.value) {
      const pageTitle = result.value?.page?.seoMeta?.name || "Explore Quality Homes from Consort Homes";
      const pageDescription = result.value?.page?.seoMeta?.description || 
        "Experience exceptional design and quality craftsmanship with Consort Homes, creating beautiful and long lasting homes in Missouri.";

      useHead({
        title: pageTitle,
        meta: [
          {
            property: "og:image",
            content: ogImageUrl,
          },
          {
            property: "og:image:secure_url",
            content: ogImageUrl,
          },
          {
            property: "og:image:width",
            content: "1200",
          },
          {
            property: "og:image:height",
            content: "630",
          },
          {
            property: "og:type",
            content: "website",
          },
          {
            property: "og:url",
            content: `${siteUrl}${route.path}`,
          },
          {
            property: "og:title",
            content: pageTitle,
          },
          {
            property: "og:description",
            content: pageDescription,
          },
          {
            name: "twitter:card",
            content: "summary_large_image",
          },
          {
            name: "twitter:image",
            content: ogImageUrl,
          },
          {
            name: "twitter:title",
            content: pageTitle,
          },
          {
            name: "twitter:description",
            content: pageDescription,
          },
        ],
      });
    }
  });
});

useHead({
  htmlAttrs: {
    class: "scroll-smooth",
  },
  link: [
    {
      rel: "icon",
      type: "image/x-icon",
      href: "/img/favicon.ico",
    },
  ],
  script: [
    {
      type: "text/javascript",
      src: "//js.hs-scripts.com/24230507.js",
      async: true,
      defer: true,
      id: "hs-script-loader",
      tagPosition: "bodyClose",
    },
    {
      type: "text/javascript",
      src: "//app.lassocrm.com/analytics.js",
      async: "true",
      tagPosition: "bodyClose",
    },
    {
      type: "text/javascript",
      innerHTML: `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-TH4RH3T')`,
    },
    {
      src: "//cdn.rlets.com/capture_configs/0ed/5be/3a1/e9b482ab21cc39fb575728b.js",
      async: true,
    },
  ],
  noscript: [
    {
      children:
        '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-TH4RH3T" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
      body: "bodyOpen",
    },
  ],
});

useLassoCRM();
</script>
